import React, { useEffect, useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Box, 
    Typography, 
    Button, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel 
} from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import { assignVehicle, getVehicle,reassignVehicle } from './action.ts';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface AssignVehicleModalProps {
  open: boolean;
  onClose: () => void;
  selectedWarehouseName: string | null;
  warehouseOptions: any;
  vehicleOptions: { id: string, label: string }[];
  onAssign: (warehouseId: string, vehicleId: string) => void;
  refreshData: () => void;
  selectedOrderIds: string[];
  selectedTripIds: string[];
  orders: any[];
}

interface AssignOrder {
  orderId: string;
}

interface ReassignOrder {
  orderId: string;
  tripId: string;
  warehouseId: string;
}

const AssignVehicleDialog = ({ 
    open, 
    onClose, 
    selectedWarehouseName,
    warehouseOptions, 
    vehicleOptions: initialVehicleOptions,
    onAssign,
    refreshData,
    selectedOrderIds, selectedTripIds, orders
}: AssignVehicleModalProps) => {
    const [selectedWarehouse, setSelectedWarehouse] = useState('');
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [vehicleOptions, setVehicleOptions] = useState(initialVehicleOptions || []);
    const [loadingVehicles, setLoadingVehicles] = useState(false);
    const userDetailsFromStorage = JSON.parse(localStorage.getItem("userdetails") || "null");
    const userRole = userDetailsFromStorage?.role?.toLowerCase(); 

    useEffect(() => {
        if(open){
            fetchVehicles(selectedWarehouse);
        }
    }, [selectedWarehouse, selectedWarehouseName, open]);

    const groupOrdersByStatus = () => {
        const assignOrders: AssignOrder[] = [];
        const reassignOrders: ReassignOrder[] = [];

        selectedOrderIds.forEach((orderId, index) => {
            const order = orders.find(o => o.id === orderId);
            
            if (order) {
                if (order.trip_id) {
                    reassignOrders.push({
                        orderId: orderId,
                        tripId: order.trip_id,
                        warehouseId: order.warehouses_id
                    });
                } else {
                    assignOrders.push({
                        orderId: orderId
                    });
                }
            }
        });

        return { assignOrders, reassignOrders };
    };

    const fetchVehicles = (warehouseId: string) => {
        setLoadingVehicles(true);
        const selectedWarehouseObj = warehouseOptions.find((option: any) => option.id === warehouseId);
        const warehouseName = selectedWarehouseObj ? selectedWarehouseObj.label : '';      
        getVehicle(
            100,
            0, 
            (response) => {
                if (response.status === 200 && response.message && response.message.vehicles_list) {
                    const options = response.message.vehicles_list.map((vehicles:any) => ({
                        id: vehicles.id,
                        label: vehicles.number_plate
                    }));
                    setVehicleOptions(options);
                } else {
                    toast.error('Failed to fetch vehicles: Invalid response format');
                    setVehicleOptions([]);
                }
                setLoadingVehicles(false);
            },
            (error) => {
                toast.error(`Failed to fetch vehicles: ${error.message || error}`);
                setVehicleOptions([]);
                setLoadingVehicles(false);
            },
            1,
            '',
            userRole !== "vendor_supervisor" ?  warehouseName : selectedWarehouseName
        );
    };

    const handleAssign = () => {
        if ((userRole !== 'vendor_supervisor') && (!selectedWarehouse || !selectedVehicle)) {
            toast.error('Please select both warehouse and vehicle');
            return;
        }
        if (!selectedOrderIds || selectedOrderIds.length === 0) {
            toast.error('No orders selected');
            return;
        }
        setIsLoading(true);
        const { assignOrders, reassignOrders } = groupOrdersByStatus();
        const isSupervisor = userRole === 'vendor_supervisor';
        const targetWarehouseId = isSupervisor ? reassignOrders[0]?.warehouseId || assignOrders[0]?.warehouseId : selectedWarehouse;

        const processAssignOrders = () => {
            if (assignOrders.length === 0) return Promise.resolve();

            if (assignOrders.length === 1) {
                return new Promise((resolve, reject) => {
                    assignVehicle(
                        assignOrders[0].orderId,
                        Number(targetWarehouseId),
                        Number(selectedVehicle),
                        (response) => {
                            resolve({ successful: 1, failed: 0 });
                        },
                        (error) => {
                            toast.error(`Failed to assign order: ${error.message || error}`);
                            reject(error);
                        }
                    );
                });
            } else {
                return new Promise((resolve, reject) => {
                    const firstOrder = assignOrders[0];
                    const remainingOrders = assignOrders.slice(1);

                    assignVehicle(
                        firstOrder.orderId,
                        Number(targetWarehouseId),
                        Number(selectedVehicle),
                        (response) => {
                            const remainingPromises = remainingOrders.map(order =>
                                new Promise((resolveRemaining, rejectRemaining) => {
                                    assignVehicle(
                                        order.orderId,
                                        Number(targetWarehouseId),
                                        Number(selectedVehicle),
                                        (response) => resolveRemaining(response),
                                        (error) => rejectRemaining(error)
                                    );
                                })
                            );

                            Promise.allSettled(remainingPromises)
                                .then((results) => {
                                    const successful = results.filter(r => r.status === 'fulfilled').length + 1;
                                    const failed = results.filter(r => r.status === 'rejected').length;
                                    
                                    if (failed === 0) {
                                        console.log(`Successfully assigned vehicle to all ${successful} orders`);
                                    } else {
                                        toast.warning(`Assignment completed: ${successful} successful, ${failed} failed`);
                                    }
                                    resolve({ successful, failed });
                                })
                                .catch((error) => {
                                    reject(error);
                                });
                        },
                        (error) => {
                            toast.error(`Failed to assign first order: ${error.message || error}`);
                            reject(error);
                        }
                    );
                });
            }
        };

        const processReassignOrders = () => {
            if (reassignOrders.length === 0) return Promise.resolve();

            if (reassignOrders.length === 1) {
                return new Promise((resolve, reject) => {
                    reassignVehicle(
                        reassignOrders[0].orderId,
                        Number(reassignOrders[0].warehouseId),
                        Number(selectedVehicle),
                        reassignOrders[0].tripId,
                        (response) => {
                            resolve({ successful: 1, failed: 0 });
                        },
                        (error) => {
                            toast.error(`Failed to reassign order: ${error.message || error}`);
                            reject(error);
                        }
                    );
                });
            } else {
                return new Promise((resolve, reject) => {
                    const firstOrder = reassignOrders[0];
                    const remainingOrders = reassignOrders.slice(1);

                    reassignVehicle(
                        firstOrder.orderId,
                        Number(firstOrder.warehouseId),
                        Number(selectedVehicle),
                        firstOrder.tripId,
                        (response) => {
                            
                            const remainingPromises = remainingOrders.map(order =>
                                new Promise((resolveRemaining, rejectRemaining) => {
                                    reassignVehicle(
                                        order.orderId,
                                        Number(order.warehouseId),
                                        Number(selectedVehicle),
                                        order.tripId,
                                        (response) => resolveRemaining(response),
                                        (error) => rejectRemaining(error)
                                    );
                                })
                            );

                            Promise.allSettled(remainingPromises)
                                .then((results) => {
                                    const successful = results.filter(r => r.status === 'fulfilled').length + 1;
                                    const failed = results.filter(r => r.status === 'rejected').length;
                                    
                                    if (failed === 0) {
                                        console.log(`Successfully reassigned vehicle to all ${successful} orders`);
                                    } else {
                                        toast.warning(`Reassignment completed: ${successful} successful, ${failed} failed`);
                                    }
                                    resolve({ successful, failed });
                                })
                                .catch((error) => {
                                    reject(error);
                                });
                        },
                        (error:any) => {
                            toast.error(`Failed to reassign first order: ${error.message || error}`);
                            reject(error);
                        }
                    );
                });
            }
        };

        Promise.allSettled([processAssignOrders(), processReassignOrders()])
            .then(([assignResults, reassignResults]) => {
                let totalSuccessful = 0;
                let totalFailed = 0;

                if (assignResults.status === 'fulfilled') {
                    totalSuccessful += assignResults.value?.successful || 0;
                    totalFailed += assignResults.value?.failed || 0;
                } else {
                    totalFailed += assignOrders.length;
                }

                if (reassignResults.status === 'fulfilled') {
                    totalSuccessful += reassignResults.value?.successful || 0;
                    totalFailed += reassignResults.value?.failed || 0;
                } else {
                    totalFailed += reassignOrders.length;
                }

                if (totalFailed === 0 && totalSuccessful > 0) {
                    toast.success(`Successfully assigned all ${totalSuccessful} orders`);
                } else if (totalSuccessful > 0) {
                    toast.warning(` ${totalSuccessful} successful, ${totalFailed} failed`);
                } else {
                    toast.error('Failed to process all orders');
                }

                refreshData?.();
                onClose();
            })
            .catch((error) => {
                toast.error(`Error during processing: ${error.message || error}`);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <Box
                sx={{
                    marginLeft: {
                        xs: '0px',
                        sm: '-180px',
                    },
                }}
            >
                <Heading 
                    title={`Assign Vehicle`} 
                    closeIcon='true' 
                    onClose={onClose}
                />
            </Box>
            <DialogContent sx={{marginTop: {xs: '-22px'}}}>
                <Box sx={{display:'flex', justifyContent:'center', flexDirection: {xs: 'column', sm: 'row'}, gap: '10px'}}>
                    {userRole !== 'vendor_supervisor' && (
                        <Box sx={{flex: 1}}>
                            <Dropdown
                                options={warehouseOptions}
                                selectedValue={selectedWarehouse}
                                handleChange={(e) => setSelectedWarehouse(e.target.value)}
                                width="250px"
                                label='Choose Warehouse'
                                placeholder='Choose Warehouse'
                                height="50px"
                            />
                        </Box>
                    )}
                    <Box sx={{flex: 1}}>
                        <Dropdown
                            options={vehicleOptions}
                            selectedValue={selectedVehicle}
                            handleChange={(e) => setSelectedVehicle(e.target.value)}
                            label='Choose Vehicle'
                            placeholder='Select a vehicle' 
                            width="250px"
                            height="50px"
                        />
                        {loadingVehicles && <Typography variant="caption">Loading vehicles...</Typography>}
                    </Box>
                </Box>
                {isLoading && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Assigning orders...
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                <Button 
                    className='custom-button' 
                    onClick={handleAssign}
                    disabled={isLoading}
                >
                    {isLoading ? `Assigning...` : `Assign Vehicle to ${selectedOrderIds.length} order(s)`}
                </Button>
                <Button 
                    className='custom-cancel-button' 
                    onClick={onClose}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignVehicleDialog;